// Server function for checking file duplicates in organization
import { createServerFn } from '@tanstack/react-start'
import { db } from '@/src/server/db'
import { z } from 'zod'
import { randomBytes, createHash } from 'crypto'
import { getSessionFromRequest } from '@/src/server/lib/accessControl'
import { sendTemplatedEmail } from '@/src/api/mail'
import { getEmailTranslation } from '@/src/lib/email-i18n'
import { getBaseUrl } from '@/src/lib/base-url'

const CheckFileDuplicateInput = z.object({
  hash: z.string().min(1, 'File hash is required'),
})

const RequestProjectAccessInput = z.object({
  projectId: z.string(),
  fileId: z.string(),
  message: z.string().optional(),
  locale: z.string().optional(),
})

const ContactProjectOwnerInput = z.object({
  projectId: z.string(),
  message: z.string().min(1, 'Message is required'),
  locale: z.string().optional(),
})

const ConfirmProjectAccessInput = z.object({
  token: z.string(),
})

export type PrivacyLevel = 'LOW' | 'MEDIUM' | 'HIGH'

export interface FileInProject {
  id: string
  shareId: string
  name: string
  description: string
  drawingCount: number
  privacy: PrivacyLevel
  isMember: boolean
  owner: {
    id: string
    name: string
    email: string
  }
}

export interface FileDuplicateInfo {
  exists: boolean
  file?: {
    id: string
    shareId: string
    name: string
    createdAt: Date
  }
  projects?: FileInProject[]
  uploadedBy?: {
    id: string
    name: string
    email: string
    uploadedAt: Date
    privacy: PrivacyLevel
  }[]
}

/**
 * Check if a file with the given hash exists in the user's organization
 * Returns information about the file and projects it's used in.
 * 
 * Privacy-aware project visibility:
 * - LOW: Projects shown, user can self-claim
 * - MEDIUM: Projects shown, user must request access via email
 * - HIGH: Projects NOT shown at all
 * 
 * SECURITY:
 * - Only returns files uploaded by users in the same organization
 * - Shows all org projects using this file (within same org)
 * - Respects privacy levels set by file uploaders
 */
export const checkFileDuplicateInOrganization = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => CheckFileDuplicateInput.parse(data))
  .handler(async ({ data }): Promise<FileDuplicateInfo> => {
    const session = await getSessionFromRequest()
    const userId = session?.user?.id
    
    if (!userId) {
      return { exists: false }
    }

    // Get current user's organization
    const currentUser = await db.user.findUnique({
      where: { id: userId },
      select: { organizationId: true },
    })

    if (!currentUser?.organizationId) {
      return { exists: false }
    }

    // Find file by hash with all related data
    const file = await db.file.findUnique({
      where: { hash: data.hash },
      select: {
        id: true,
        shareId: true,
        name: true,
        createdAt: true,
        userFiles: {
          where: { deletedAt: null },
          include: {
            user: {
              select: {
                id: true,
                name: true,
                email: true,
                organizationId: true,
              },
            },
          },
        },
        projects: {
          include: {
            createdBy: {
              select: { id: true, name: true, email: true, organizationId: true },
            },
            users: {
              select: { id: true },
            },
            _count: {
              select: { drawings: true },
            },
          },
        },
      },
    })

    if (!file) {
      return { exists: false }
    }

    // Check if any uploader is from the same organization
    const organizationUploaders = file.userFiles.filter(
      (uf) => uf.user.organizationId === currentUser.organizationId
    )

    if (organizationUploaders.length === 0) {
      return { exists: false }
    }

    // Determine the effective privacy for showing projects.
    // Use the most restrictive privacy from any org uploader.
    const privacyOrder: Record<string, number> = { LOW: 0, MEDIUM: 1, HIGH: 2 }
    const maxOrgPrivacy = organizationUploaders.reduce((max, uf) => {
      const level = uf.privacy as PrivacyLevel
      return privacyOrder[level] > privacyOrder[max] ? level : max
    }, 'LOW' as PrivacyLevel)

    // Get all org projects using this file (regardless of user's access)
    // Filter: project creator must be in the same organization
    const orgProjects = file.projects.filter((project) => {
      return project.createdBy.organizationId === currentUser.organizationId
    })

    // Build project info based on privacy:
    // HIGH → don't include any projects
    // LOW/MEDIUM → include with privacy level and owner info
    let projectsInfo: FileInProject[] = []
    if (maxOrgPrivacy !== 'HIGH') {
      projectsInfo = orgProjects.map((project) => {
        const isMember = project.createdById === userId || 
          project.users.some((u) => u.id === userId)
        
        return {
          id: project.id,
          shareId: project.shareId,
          name: project.name,
          description: project.description,
          drawingCount: project._count.drawings,
          privacy: maxOrgPrivacy,
          isMember,
          owner: {
            id: project.createdBy.id,
            name: project.createdBy.name,
            email: project.createdBy.email,
          },
        }
      })
    }

    const uploadersInfo = organizationUploaders.map((uf) => ({
      id: uf.user.id,
      name: uf.user.name,
      email: uf.user.email,
      uploadedAt: uf.createdAt,
      privacy: uf.privacy as PrivacyLevel,
    }))

    return {
      exists: true,
      file: {
        id: file.id,
        shareId: file.shareId,
        name: file.name,
        createdAt: file.createdAt,
      },
      projects: projectsInfo,
      uploadedBy: uploadersInfo,
    }
  })

/**
 * Request access to a project via email.
 * Used when file privacy is MEDIUM — sends an email to the project owner
 * with a one-click confirmation link.
 */
export const requestProjectAccess = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => RequestProjectAccessInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const userId = session?.user?.id
    const userName = session?.user?.name
    const userEmail = session?.user?.email
    
    if (!userId || !userName || !userEmail) {
      throw new Error('Not authenticated')
    }

    // Verify project exists and get owner info
    const project = await db.project.findUnique({
      where: { id: data.projectId },
      include: {
        createdBy: { select: { id: true, name: true, email: true, preferences: true } },
        users: { select: { id: true } },
      },
    })

    if (!project) {
      throw new Error('Project not found')
    }

    // If already a member, use contactProjectOwner instead
    const isMember = project.createdById === userId || 
      project.users.some((u) => u.id === userId)
    if (isMember) {
      throw new Error('You already have access to this project. Use the contact feature to send a message.')
    }

    // Generate a secure token for the confirmation link
    const token = randomBytes(32).toString('hex')
    const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // 7 days

    // Store the token in the Verification table
    await db.verification.create({
      data: {
        id: randomBytes(16).toString('hex'),
        identifier: `project-access:${data.projectId}:${userId}`,
        value: token,
        expiresAt,
        createdAt: new Date(),
        updatedAt: new Date(),
      },
    })

    // Build the confirmation URL
    const baseUrl = getBaseUrl()
    const confirmUrl = `${baseUrl}/api/confirm-project-access?token=${token}`

    // Use the sender's chosen locale, fall back to owner's locale, then 'en'
    const ownerPrefs = (project.createdBy as any).preferences as Record<string, any> | null
    const emailLocale = data.locale || ownerPrefs?.locale || 'en'
    const vars = { userName: userName!, userEmail: userEmail!, projectName: project.name }

    // Send email to project owner (translated)
    const [subject, title, body, messageLabel, sharedFileNotice, ctaText, footerNotice] = await Promise.all([
      getEmailTranslation(emailLocale, 'accessRequest.subject', vars),
      getEmailTranslation(emailLocale, 'accessRequest.title', vars),
      getEmailTranslation(emailLocale, 'accessRequest.body', vars),
      getEmailTranslation(emailLocale, 'accessRequest.messageLabel', vars),
      getEmailTranslation(emailLocale, 'accessRequest.sharedFileNotice', vars),
      getEmailTranslation(emailLocale, 'accessRequest.ctaText', vars),
      getEmailTranslation(emailLocale, 'accessRequest.footerNotice', vars),
    ])

    await sendTemplatedEmail({
      to: project.createdBy.email,
      subject,
      replyTo: userEmail,
      template: {
        title,
        body: `
          ${body}
          <div style="background: #f8f9fa; border-radius: 8px; padding: 16px; margin: 16px 0;">
            <p style="margin: 0; font-weight: 600;">${project.name}</p>
            ${project.description ? `<p style="margin: 4px 0 0; color: #6b7280; font-size: 14px;">${project.description}</p>` : ''}
          </div>
          ${data.message ? `<p><strong>${messageLabel}</strong></p><p style="background: #f8f9fa; border-radius: 8px; padding: 12px;">${data.message.replace(/\n/g, '<br>')}</p>` : ''}
          <p>${sharedFileNotice}</p>
        `,
        ctaText,
        ctaUrl: confirmUrl,
        footerNotice,
      },
    })

    // Log the access request
    await db.logEntry.create({
      data: {
        type: 'ProjectAccessRequest',
        userId,
        details: {
          projectId: project.id,
          projectName: project.name,
          requestedBy: userName,
          requestedByEmail: userEmail,
          ownerId: project.createdBy.id,
          ownerEmail: project.createdBy.email,
        },
      },
    }).catch(err => console.error('Failed to create log entry:', err))

    return { success: true }
  })

/**
 * Contact a project owner directly via email.
 * Used for any privacy level — allows sending a message to discuss
 * the shared file, coordinate work, or set up meetings.
 * Does NOT request access or create a claim token.
 */
export const contactProjectOwner = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => ContactProjectOwnerInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    const userId = session?.user?.id
    const userName = session?.user?.name
    const userEmail = session?.user?.email
    
    if (!userId || !userName || !userEmail) {
      throw new Error('Not authenticated')
    }

    const project = await db.project.findUnique({
      where: { id: data.projectId },
      include: {
        createdBy: { select: { id: true, name: true, email: true, preferences: true } },
      },
    })

    if (!project) {
      throw new Error('Project not found')
    }

    // Don't let someone email themselves
    if (project.createdById === userId) {
      throw new Error('You are the owner of this project')
    }

    // Use the sender's chosen locale, fall back to owner's locale, then 'en'
    const ownerPrefs = (project.createdBy as any).preferences as Record<string, any> | null
    const emailLocale = data.locale || ownerPrefs?.locale || 'en'
    const vars = { userName: userName!, userEmail: userEmail!, projectName: project.name }

    const [subject, title, body, replyNotice, footerNotice] = await Promise.all([
      getEmailTranslation(emailLocale, 'contactOwner.subject', vars),
      getEmailTranslation(emailLocale, 'contactOwner.title', vars),
      getEmailTranslation(emailLocale, 'contactOwner.body', vars),
      getEmailTranslation(emailLocale, 'contactOwner.replyNotice', vars),
      getEmailTranslation(emailLocale, 'contactOwner.footerNotice', vars),
    ])

    await sendTemplatedEmail({
      to: project.createdBy.email,
      subject,
      replyTo: userEmail,
      template: {
        title,
        body: `
          ${body}
          <div style="background: #f8f9fa; border-radius: 8px; padding: 16px; margin: 16px 0;">
            <p style="margin: 0; font-weight: 600;">${project.name}</p>
            ${project.description ? `<p style="margin: 4px 0 0; color: #6b7280; font-size: 14px;">${project.description}</p>` : ''}
          </div>
          <div style="background: #f8f9fa; border-radius: 8px; padding: 16px; margin: 16px 0;">
            <p style="margin: 0; white-space: pre-wrap;">${data.message.replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>')}</p>
          </div>
          <p>${replyNotice}</p>
        `,
        footerNotice,
      },
    })

    await db.logEntry.create({
      data: {
        type: 'ProjectOwnerContacted',
        userId,
        details: {
          projectId: project.id,
          projectName: project.name,
          contactedBy: userName,
          contactedByEmail: userEmail,
          ownerId: project.createdBy.id,
          ownerEmail: project.createdBy.email,
        },
      },
    }).catch(err => console.error('Failed to create log entry:', err))

    return { success: true }
  })

/**
 * Confirm a project access request (called from email link).
 * Validates the token and adds the requesting user to the project.
 */
export const confirmProjectAccess = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => ConfirmProjectAccessInput.parse(data))
  .handler(async ({ data }) => {
    // Find the verification token
    const verification = await db.verification.findFirst({
      where: {
        value: data.token,
        identifier: { startsWith: 'project-access:' },
        expiresAt: { gt: new Date() },
      },
    })

    if (!verification) {
      throw new Error('Invalid or expired access token')
    }

    // Parse the identifier: "project-access:{projectId}:{requestingUserId}"
    const parts = verification.identifier.split(':')
    if (parts.length !== 3) {
      throw new Error('Invalid token format')
    }

    const [, projectId, requestingUserId] = parts

    // Verify project still exists
    const project = await db.project.findUnique({
      where: { id: projectId },
      include: { users: { select: { id: true } } },
    })

    if (!project) {
      throw new Error('Project no longer exists')
    }

    // Check if user is already a member
    const alreadyMember = project.createdById === requestingUserId || 
      project.users.some((u) => u.id === requestingUserId)

    if (!alreadyMember) {
      // Add the requesting user to the project
      await db.project.update({
        where: { id: projectId },
        data: { users: { connect: { id: requestingUserId } } },
      })
    }

    // Clean up the verification token
    await db.verification.delete({ where: { id: verification.id } })

    // Log the access grant
    await db.logEntry.create({
      data: {
        type: 'ProjectAccessGranted',
        userId: requestingUserId,
        details: {
          projectId: project.id,
          projectName: project.name,
          grantedTo: requestingUserId,
        },
      },
    }).catch(err => console.error('Failed to create log entry:', err))

    return { 
      success: true, 
      projectName: project.name,
      alreadyMember,
    }
  })
